import React, { useContext, useMemo, useState, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useLazyProductQuery } from '~/techstyle-shared/react-products';

import {
  QuickViewProductCategories,
  InseamValues,
} from '../../constants/productDetail';

const Context = React.createContext();
export const useQuickViewContext = () => {
  return useContext(Context);
};

const getProductCategory = (item) => {
  const productCategories = QuickViewProductCategories;
  if (productCategories.size_shoe.has(item)) {
    return 'size_shoe';
  }
  if (productCategories.size_undie.has(item)) {
    return 'size_undie';
  }
  if (productCategories.size_bottom.has(item)) {
    return 'size_bottom';
  }
  if (productCategories.size_bra.has(item)) {
    return 'size_bra';
  }
  if (productCategories.size_top.has(item)) {
    return 'size_top';
  }
  return null;
};

const buildInseamOption = (instanceLabel) => {
  /* This is a  parser function to extract inseam value */
  let [, attributeLevel1] = instanceLabel.split('/');
  if (attributeLevel1) {
    attributeLevel1 = attributeLevel1.trim();
    if (InseamValues[attributeLevel1]) {
      return InseamValues[attributeLevel1];
    } else {
      let [attributeLevel2] = attributeLevel1.split('|');
      attributeLevel2 = attributeLevel2.trim();
      if (InseamValues[attributeLevel2]) {
        return InseamValues[attributeLevel2];
      }
    }
  }
};

export default function QuickViewContext({ children, isCheckout = false }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permalink, setPermalink] = useState('');
  const [psrc, setPsrc] = useState('');
  const [product, setProduct] = useState({});
  const [cartItem, setCartItem] = useState({});
  const [getProduct, queryData] = useLazyProductQuery({
    permalink: permalink,
  });

  const openQuickViewModal = useCallback(
    async (event, params) => {
      event.preventDefault();
      const { product, cartItem, psrc } = params;
      if (isCheckout) {
        setPermalink(cartItem.permalink);
        setCartItem(cartItem);
      } else {
        setPermalink(product.permalink);
        setProduct(product);
      }
      if (psrc) {
        setPsrc(psrc);
      }
      getProduct();
      setIsModalOpen(true);
    },
    [getProduct, isCheckout]
  );

  const closeQuickViewModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const updatePermalink = useCallback((newPermalink) => {
    setPermalink(newPermalink);
  }, []);

  const getSelectedCartBundleSizes = useCallback(() => {
    const sizes = {};
    if (cartItem && cartItem.bundleItems) {
      cartItem.bundleItems.forEach((item) => {
        const itemSizes = {};
        let isCategoryFound = false;
        item.productCategoryIdList.forEach((productCategory) => {
          if (!isCategoryFound) {
            const category = getProductCategory(productCategory);
            if (category) {
              itemSizes[category] = item.size;
              isCategoryFound = true;
            }
          }
        });
        itemSizes.Inseam = buildInseamOption(item.instanceLabel);
        const masterProductId = item.masterProductId;
        sizes[masterProductId] = itemSizes;
      });
    }
    return sizes;
  }, [cartItem]);

  const getSelectedCartItemSizes = useCallback(() => {
    const sizes = {};
    let isCategoryFound = false;
    const isCartItemPresent = Boolean(cartItem.masterProductId);
    const isBundlePresent = cartItem.bundleItems?.length;

    if (isCartItemPresent && !isBundlePresent) {
      cartItem.productCategoryIdList.forEach((item) => {
        if (!isCategoryFound) {
          const category = getProductCategory(item);
          if (category) {
            sizes[category] = cartItem.size;
            isCategoryFound = true;
          }
        }
      });
      sizes.Inseam = buildInseamOption(cartItem.instanceLabel);
    }
    return sizes;
  }, [cartItem]);

  const context = useMemo(
    () => ({
      isCheckout,
      openQuickViewModal,
      closeQuickViewModal,
      isModalOpen,
      getProduct,
      queryData,
      psrc,
      updatePermalink,
      product,
      cartItem,
      getSelectedCartBundleSizes,
      getSelectedCartItemSizes,
    }),
    [
      isModalOpen,
      openQuickViewModal,
      closeQuickViewModal,
      isCheckout,
      getProduct,
      queryData,
      psrc,
      updatePermalink,
      product,
      cartItem,
      getSelectedCartBundleSizes,
      getSelectedCartItemSizes,
    ]
  );

  return (
    <Context.Provider value={context}>
      {typeof children === 'function' ? children(context) : children}
    </Context.Provider>
  );
}

QuickViewContext.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isCheckout: PropTypes.bool,
};
